import React from "react";
import { useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Card, Tag, Tabs, Spin } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import {
    AppstoreOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    TagOutlined,
    DollarOutlined,
    InboxOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/ui";
import { StockMovementSummary } from "./components/StockMovementSummary";
import dayjs from "dayjs";

interface Price {
    id: number;
    basePrice: number;
    effectivePrice: number;
    discountType: string;
    discountValue: number;
    isActive: boolean;
    currencyCode: string;
    startDate: string;
    endDate: string | null;
}

export const InventoryShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { queryResult } = useShow({
        resource: "inventory",
        id,
    });

    const { data, isLoading } = queryResult;
    const record = data?.data;

    // Find the first active price
    const activePrice = record?.prices?.find((price: Price) => price.isActive);

    // Stock level information
    const currentStock = record?.stockLevel?.currentStockLevel ?? 0;
    const reorderPoint = record?.stockLevel?.reorderPoint ?? 0;
    const isLowStock = currentStock <= reorderPoint;
    const availableStock = record?.stockLevel?.availableStock ?? 0;

    const formatCurrency = (amount: number | undefined, currency: string = "USD") => {
        if (amount === undefined || amount === null) return '-';
        return `${currency} ${amount.toFixed(2)}`;
    };

    if (isLoading) {
        return (
            <div className="page-container flex items-center justify-center" style={{ minHeight: 400 }}>
                <Spin size="large" />
            </div>
        );
    }

    const tabItems = [
        {
            key: 'overview',
            label: 'Overview',
            children: (
                <div className="space-y-4">
                    {/* Basic Information Card */}
                    <div className="info-card">
                        <div className="info-card-header">
                            <h3 className="info-card-title">Basic Information</h3>
                        </div>
                        <div className="info-card-body">
                            <div className="info-grid">
                                <div className="info-item">
                                    <span className="info-label">Item Code:</span>
                                    <span className="info-value font-semibold">{record?.itemCode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Item Name:</span>
                                    <span className="info-value">{record?.itemName || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Type:</span>
                                    <span className="info-value">{record?.itemTypeCode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Classification:</span>
                                    <span className="info-value">{record?.itemClassificationCode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Barcode:</span>
                                    <span className="info-value">{record?.barcode || "-"}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Status:</span>
                                    <span className={`status-badge ${record?.isActive ? 'active' : 'inactive'}`}>
                                        {record?.isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                        {record?.isActive ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Created At:</span>
                                    <span className="info-value">
                                        <DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Last Updated:</span>
                                    <span className="info-value">
                                        <DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm:ss" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stock Information Card */}
                    {record?.stockLevel && (
                        <div className="info-card">
                            <div className="info-card-header">
                                <h3 className="info-card-title">
                                    <InboxOutlined style={{ marginRight: 8 }} />
                                    Stock Information
                                </h3>
                            </div>
                            <div className="info-card-body">
                                <div className="info-grid">
                                    <div className="info-item">
                                        <span className="info-label">Current Stock:</span>
                                        <span className={`info-value font-semibold ${isLowStock ? 'text-danger' : 'text-success'}`}>
                                            {currentStock.toFixed(2)}
                                            {isLowStock && <WarningOutlined style={{ marginLeft: 8, color: 'var(--color-danger)' }} />}
                                        </span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Available Stock:</span>
                                        <span className="info-value">{availableStock.toFixed(2)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Reserved:</span>
                                        <span className="info-value">{(record.stockLevel.reservedStock ?? 0).toFixed(2)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Reorder Point:</span>
                                        <span className="info-value">{reorderPoint.toFixed(2)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Min Quantity:</span>
                                        <span className="info-value">{(record.stockLevel.minimumQuantity ?? 0).toFixed(2)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Max Quantity:</span>
                                        <span className="info-value">{(record.stockLevel.maximumQuantity ?? 0).toFixed(2)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Stock Status:</span>
                                        <Tag color={isLowStock ? "error" : "success"}>
                                            {isLowStock ? "Low Stock" : "In Stock"}
                                        </Tag>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Last Updated:</span>
                                        <span className="info-value">
                                            {record.stockLevel.lastUpdated ? dayjs(record.stockLevel.lastUpdated).format("YYYY-MM-DD HH:mm") : "-"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tax Information Card */}
                    {record?.tax && (
                        <div className="info-card">
                            <div className="info-card-header">
                                <h3 className="info-card-title">
                                    <TagOutlined style={{ marginRight: 8 }} />
                                    Tax Information
                                </h3>
                            </div>
                            <div className="info-card-body">
                                <div className="info-grid">
                                    <div className="info-item">
                                        <span className="info-label">Tax Type:</span>
                                        <span className="info-value">{record.tax.taxType}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Tax Rate:</span>
                                        <span className="info-value font-semibold text-primary">{record.tax.rate}%</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Tax Status:</span>
                                        <Tag color={record.tax.isActive ? "success" : "default"}>
                                            {record.tax.isActive ? "Active" : "Inactive"}
                                        </Tag>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: 'pricing',
            label: (
                <span>
                    <DollarOutlined style={{ marginRight: 8 }} />
                    Pricing
                </span>
            ),
            children: (
                <div className="space-y-4">
                    {activePrice ? (
                        <div className="info-card">
                            <div className="info-card-header">
                                <h3 className="info-card-title">Active Price</h3>
                            </div>
                            <div className="info-card-body">
                                <div className="info-grid">
                                    <div className="info-item">
                                        <span className="info-label">Base Price:</span>
                                        <span className="info-value">{formatCurrency(activePrice.basePrice, activePrice.currencyCode)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Effective Price:</span>
                                        <span className="info-value font-semibold text-primary">{formatCurrency(activePrice.effectivePrice, activePrice.currencyCode)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Discount Type:</span>
                                        <span className="info-value">{activePrice.discountType || "None"}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Discount Value:</span>
                                        <span className="info-value">
                                            {activePrice.discountType === "Percentage" 
                                                ? `${activePrice.discountValue}%` 
                                                : formatCurrency(activePrice.discountValue, activePrice.currencyCode)}
                                        </span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Currency:</span>
                                        <span className="info-value">{activePrice.currencyCode}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Status:</span>
                                        <Tag color="success">Active</Tag>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Valid From:</span>
                                        <span className="info-value">{activePrice.startDate ? dayjs(activePrice.startDate).format("YYYY-MM-DD") : "-"}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Valid Until:</span>
                                        <span className="info-value">{activePrice.endDate ? dayjs(activePrice.endDate).format("YYYY-MM-DD") : "No End Date"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Card className="data-table-card">
                            <div className="text-center py-8 text-text-muted">
                                No active price information available for this item.
                            </div>
                        </Card>
                    )}
                </div>
            ),
        },
        {
            key: 'movements',
            label: (
                <span>
                    <InboxOutlined style={{ marginRight: 8 }} />
                    Stock Movements
                </span>
            ),
            children: record?.id ? (
                <StockMovementSummary productId={record.id} />
            ) : (
                <div className="text-center py-8 text-text-muted">No stock movement data available</div>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title={record?.itemName || "Inventory Item"}
                titleIcon={<AppstoreOutlined />}
                breadcrumbs={[
                    { title: "Inventory", href: "/inventory" },
                    { title: record?.itemName || "Details" },
                ]}
                actions={[
                    {
                        label: "Edit",
                        onClick: () => navigate(`/inventory/edit/${id}`),
                        type: "primary",
                        icon: <EditOutlined />,
                    },
                ]}
            >
                {/* Mini Stat Cards */}
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Item Code</div>
                        <div className="mini-stat-value">{record?.itemCode || "-"}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Current Stock</div>
                        <div className={`mini-stat-value ${isLowStock ? 'text-danger' : ''}`}>
                            {currentStock.toFixed(0)}
                        </div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Stock Status</div>
                        <Tag color={isLowStock ? "error" : "success"}>
                            {isLowStock ? "Low Stock" : "In Stock"}
                        </Tag>
                    </div>
                    {activePrice && (
                        <div className="mini-stat-card">
                            <div className="mini-stat-label">Effective Price</div>
                            <div className="mini-stat-value">{formatCurrency(activePrice.effectivePrice, activePrice.currencyCode)}</div>
                        </div>
                    )}
                </div>
            </PageHeader>

            {/* Tabs */}
            <div className="tab-card">
                <Tabs defaultActiveKey="overview" items={tabItems} className="custom-tabs" />
            </div>
        </div>
    );
};
